import { useEffect } from 'react';
import isMobileLayout from '../utils/isMobileLayout';

export default function useCustomCursor({ disableCustomCursor = false, disableEasing = false } = {}) {
  useEffect(() => {
    // Disable on phones and tablets — the floating arrow only makes sense
    // with a real mouse. `isMobileLayout` covers both narrow widths and
    // wide tablets (which report `pointer: coarse`).
    if (isMobileLayout()) {
      return;
    }

    // Admin kill-switch: skip the custom cursor entirely and tag <body> so
    // the CSS `cursor: none` overrides surrender to the platform default.
    // Also strip any cursor-container left over from a previous run of this
    // effect (e.g. the initial render installed the cursor before the
    // fetched setting arrived and disabled it) — otherwise it sits in the
    // DOM frozen at the last RAF position.
    if (disableCustomCursor) {
      document.body.classList.add('cursor-default');
      const leftover = document.getElementById('cursor-container');
      if (leftover && leftover.parentNode) leftover.parentNode.removeChild(leftover);
      return () => document.body.classList.remove('cursor-default');
    }
    document.body.classList.remove('cursor-default');

    const S = 34;
    const P = "M3 3.5 L11.5 21 L12.5 14 L20 10.5 Z";
    const sc = S / 24;

    const arrow = (f) =>
      `<svg width="24" height="24" viewBox="0 0 24 24"><path d="${P}" fill="#0A0A0A" stroke="#0A0A0A" stroke-width="6.8" stroke-linejoin="round" stroke-linecap="round"/><path d="${P}" fill="${f}" stroke="${f}" stroke-width="3.2" stroke-linejoin="round" stroke-linecap="round"/></svg>`;

    let container = document.getElementById('cursor-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'cursor-container';
      document.body.appendChild(container);
    }

    container.innerHTML = `<div style="transform:translate(${-3 * sc}px,${-3.5 * sc}px) scale(${sc});transform-origin:0 0;position:absolute"><div class="u">${arrow('#3FA9E8')}</div><div class="t">${arrow('#e0eaf5')}</div></div>`;

    let tx = -99,
      ty = -99,
      cx = -99,
      cy = -99;

    const handlePointerMove = (e) => {
      tx = e.clientX;
      ty = e.clientY;
    };

    let rafId = 0;
    const loop = () => {
      if (cx < -50 || disableEasing) {
        // First frame, or admin disabled smoothing — snap to the pointer.
        cx = tx;
        cy = ty;
      } else {
        // Easing factor — higher = snappier and closer to the real mouse.
        // History: 0.12 was the original "floaty" feel (too laggy);
        // 0.35 felt almost direct (smoothing invisible); 0.22 is the
        // middle ground — clearly smoothed but still tracking close to
        // the physical pointer. Don't go to 1.0 (twitchy) or below ~0.15
        // (laggy enough customers complain).
        cx += (tx - cx) * 0.22;
        cy += (ty - cy) * 0.22;
      }
      container.style.transform = `translate3d(${cx}px,${cy}px,0)`;
      rafId = requestAnimationFrame(loop);
    };

    const handlePointerDown = (e) => {
      container.classList.add('clicked');
      const ox = e.clientX + 8 * sc;
      const oy = e.clientY + 7.5 * sc;
      const d = document.createElement('div');
      d.style.cssText = `position:fixed;left:${ox}px;top:${oy}px;pointer-events:none;z-index:10000`;

      for (let i = 0; i < 5; i++) {
        const a = ((30 + 180 - 80 + i * 40) * Math.PI) / 180;
        const L = document.createElement('div');
        L.className = 'cursor-line';
        L.style.width = '14px';
        L.style.setProperty('--angle', a + 'rad');
        d.appendChild(L);
      }
      document.body.appendChild(d);
      setTimeout(() => {
        d.remove();
      }, 460);
    };

    const handlePointerUp = () => {
      container.classList.remove('clicked');
    };

    addEventListener('pointermove', handlePointerMove);
    addEventListener('pointerdown', handlePointerDown);
    addEventListener('pointerup', handlePointerUp);
    rafId = requestAnimationFrame(loop);

    return () => {
      removeEventListener('pointermove', handlePointerMove);
      removeEventListener('pointerdown', handlePointerDown);
      removeEventListener('pointerup', handlePointerUp);
      cancelAnimationFrame(rafId);
      // Remove the container too so a settings flip can't leave a frozen
      // arrow in the DOM. (Bail-branch re-init will also strip leftovers,
      // but cleaning up here keeps unmount tidy.)
      if (container && container.parentNode) container.parentNode.removeChild(container);
    };
  }, [disableCustomCursor, disableEasing]);
}
